import React, { useState } from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import contactHeroImage from '../../assets/pages-hero/contact.png';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert(language === 'tr' ? 'Mesajınız için teşekkür ederiz. En kısa sürede size dönüş yapacağız!' : 'Thank you for your message. We will get back to you soon!');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    { icon: '📍', title: language === 'tr' ? 'Genel Merkez' : 'Headquarters', details: [language === 'tr' ? 'Atatürk Bulvarı No: 123' : 'Atatürk Blvd. No: 123', 'Çankaya, Ankara 06680', 'Türkiye'] },
    { icon: '📧', title: language === 'tr' ? 'E-posta' : 'Email', details: ['info@turkicsilkroad.org', 'press@turkicsilkroad.org'] },
    { icon: '📞', title: language === 'tr' ? 'Telefon' : 'Phone', details: ['+90 312 XXX XX XX', language === 'tr' ? '+90 312 XXX XX XX (Faks)' : '+90 312 XXX XX XX (Fax)'] },
    { icon: '🕐', title: language === 'tr' ? 'Çalışma Saatleri' : 'Working Hours', details: [language === 'tr' ? 'Pazartesi - Cuma' : 'Monday - Friday', '09:00 - 18:00 (GMT+3)'] },
  ];

  const offices = [
    { city: 'Ankara', country: 'Türkiye', flag: FLAGS.turkey, type: language === 'tr' ? 'Genel Merkez' : 'Headquarters' },
    { city: language === 'tr' ? 'Bakü' : 'Baku', country: language === 'tr' ? 'Azerbaycan' : 'Azerbaijan', flag: FLAGS.azerbaijan, type: language === 'tr' ? 'Bölge Ofisi' : 'Regional Office' },
    { city: 'Astana', country: language === 'tr' ? 'Kazakistan' : 'Kazakhstan', flag: FLAGS.kazakhstan, type: language === 'tr' ? 'Bölge Ofisi' : 'Regional Office' },
    { city: language === 'tr' ? 'Bişkek' : 'Bishkek', country: language === 'tr' ? 'Kırgızistan' : 'Kyrgyzstan', flag: FLAGS.kyrgyzstan, type: language === 'tr' ? 'Bölge Ofisi' : 'Regional Office' },
    { city: language === 'tr' ? 'Taşkent' : 'Tashkent', country: language === 'tr' ? 'Özbekistan' : 'Uzbekistan', flag: FLAGS.uzbekistan, type: language === 'tr' ? 'Bölge Ofisi' : 'Regional Office' },
  ];

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={{
        ...styles.hero,
        background: isDarkMode 
          ? `linear-gradient(135deg, ${DARK_THEME.background}DD 0%, ${DARK_THEME.backgroundTertiary}DD 100%), url(${contactHeroImage})`
          : `linear-gradient(135deg, ${COLORS.primary}DD 0%, ${COLORS.dark}DD 100%), url(${contactHeroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'multiply',
      }}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : 'rgba(255,255,255,0.15)',
          }}>{language === 'tr' ? 'Bize Ulaşın' : 'Get in Touch'}</span>
          <h1 style={styles.heroTitle}>{language === 'tr' ? 'İletişim' : 'Contact Us'}</h1>
          <p style={styles.heroSubtitle}>
            {language === 'tr'
              ? 'Sizden haber almaktan memnuniyet duyarız. Sorularınız, iş birliği teklifleriniz veya basın talepleriniz için bize ulaşın.'
              : "We'd love to hear from you. Reach out for inquiries, partnerships, or media requests."}
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section style={{
        ...styles.section,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={styles.container}>
          <div style={styles.contactGrid}>
            {/* Contact Info */}
            <div style={styles.infoSection}>
              <h2 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                {language === 'tr' ? 'İletişim Bilgileri' : 'Contact Information'}
              </h2>
              <p style={{...styles.sectionSubtitle, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                {language === 'tr' ? 'Türk Devletleri Teşkilatı ile iletişime geçin' : 'Get in touch with the Organization of Turkic States'}
              </p>

              <div style={styles.infoCards}>
                {contactInfo.map((info, index) => (
                  <div key={index} style={{
                    ...styles.infoCard,
                    backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light,
                  }}>
                    <span style={styles.infoIcon}>{info.icon}</span>
                    <div>
                      <h3 style={{...styles.infoTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                        {info.title}
                      </h3>
                      {info.details.map((detail, idx) => (
                        <p key={idx} style={{...styles.infoDetail, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div style={{
                ...styles.socialSection,
                borderTop: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`,
              }}>
                <h3 style={{...styles.socialTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                  {language === 'tr' ? 'Bizi Takip Edin' : 'Follow Us'}
                </h3>
                <div style={styles.socialLinks}>
                  {[
                    {
                      icon: '𝕏',
                      name: 'Twitter',
                      url: 'https://twitter.com/Turkic_States'
                    },
                    {
                      icon: 'f',
                      name: 'Facebook',
                      url: 'https://www.facebook.com/turkicstates/'
                    },
                    {
                      icon: 'in',
                      name: 'LinkedIn',
                      url: 'https://www.linkedin.com/company/organizationofturkicstates/'
                    },
                    {
                      icon: '📷',
                      name: 'Instagram',
                      url: 'https://www.instagram.com/organizationofturkicstates/'
                    },
                    {
                      icon: '▶️',
                      name: 'YouTube',
                      url: 'https://www.youtube.com/organizationofturkicstates'
                    }
                  ].map((social, idx) => (
                    <a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...styles.socialLink,
                        backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light,
                        color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700,
                      }}
                    >
                      <span style={styles.socialIcon}>{social.icon}</span>
                      {social.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div style={styles.formSection}>
              <div style={{
                ...styles.formCard,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light,
              }}>
                <h2 style={{...styles.formTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                  {language === 'tr' ? 'Bize Mesaj Gönderin' : 'Send us a Message'}
                </h2>
                <form onSubmit={handleSubmit} style={styles.form}>
                  <div style={styles.formRow}>
                    <div style={styles.inputGroup}>
                      <label style={{...styles.label, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                        {language === 'tr' ? 'Ad Soyad *' : 'Full Name *'}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'tr' ? 'Adınızı girin' : 'Enter your name'}
                        style={{
                          ...styles.input,
                          backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.white,
                          borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300,
                          color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                        }}
                        required
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={{...styles.label, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                        {language === 'tr' ? 'E-posta Adresi *' : 'Email Address *'}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={language === 'tr' ? 'E-posta adresinizi girin' : 'Enter your email'}
                        style={{
                          ...styles.input,
                          backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.white,
                          borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300,
                          color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={{...styles.label, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                      {language === 'tr' ? 'Konu *' : 'Subject *'}
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      style={{
                        ...styles.select,
                        backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.white,
                        borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300,
                        color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                      }}
                      required
                    >
                      <option value="">{language === 'tr' ? 'Bir konu seçin' : 'Select a subject'}</option>
                      <option value="general">{language === 'tr' ? 'Genel Bilgi Talebi' : 'General Inquiry'}</option>
                      <option value="partnership">{language === 'tr' ? 'İş Birliği Fırsatı' : 'Partnership Opportunity'}</option>
                      <option value="media">{language === 'tr' ? 'Medya ve Basın' : 'Media & Press'}</option>
                      <option value="events">{language === 'tr' ? 'Etkinlikler ve Programlar' : 'Events & Programs'}</option>
                      <option value="other">{language === 'tr' ? 'Diğer' : 'Other'}</option>
                    </select>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={{...styles.label, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                      {language === 'tr' ? 'Mesaj *' : 'Message *'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={language === 'tr' ? 'Mesajınızı buraya yazın...' : 'Write your message here...'}
                      style={{
                        ...styles.textarea,
                        backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.white,
                        borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300,
                        color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                      }}
                      rows={6}
                      required
                    />
                  </div>

                  <button type="submit" style={{
                    ...styles.submitBtn,
                    backgroundColor: isDarkMode ? DARK_THEME.primary : COLORS.primary,
                  }}>
                    {language === 'tr' ? 'Mesaj Gönder' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section style={{
        ...styles.officesSection,
        backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
      }}>
        <div style={styles.container}>
          <div style={styles.header}>
            <span style={{
              ...styles.badgeSecondary,
              backgroundColor: isDarkMode ? `${DARK_THEME.secondary}30` : `${COLORS.secondary}20`,
              color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary,
            }}>{language === 'tr' ? 'Temsilciliklerimiz' : 'Our Presence'}</span>
            <h2 style={{...styles.sectionTitleCenter, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
              {language === 'tr' ? 'Bölge Ofisleri' : 'Regional Offices'}
            </h2>
          </div>
          <div style={styles.officesGrid}>
            {offices.map((office, index) => (
              <div key={index} style={{
                ...styles.officeCard,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.06)',
              }}>
                <img src={office.flag} alt={office.country} style={styles.officeFlagImage} />
                <h3 style={{...styles.officeCity, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                  {office.city}
                </h3>
                <p style={{...styles.officeCountry, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {office.country}
                </p>
                <span style={{
                  ...styles.officeType,
                  backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : `${COLORS.primary}15`,
                  color: isDarkMode ? DARK_THEME.primary : COLORS.primary,
                }}>{office.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section style={{
        ...styles.mapSection,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={{
          ...styles.mapPlaceholder,
          backgroundColor: isDarkMode ? DARK_THEME.backgroundTertiary : COLORS.dark,
        }}>
          <span style={styles.mapIcon}>🗺️</span>
          <h3 style={styles.mapTitle}>{language === 'tr' ? 'Etkileşimli Harita' : 'Interactive Map'}</h3>
          <p style={styles.mapText}>{language === 'tr' ? 'TDT genel merkezi ile bölge ofislerini gösteren harita entegrasyonu' : 'Map integration showing OTS headquarters and regional offices'}</p>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { paddingTop: '0px' },
  hero: {
    height: '50vh',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'rgba(255,255,255,0.03)\'/%3E%3C/svg%3E")',
  },
  heroContent: { position: 'relative', zIndex: 2, padding: '0 20px' },
  badge: { display: 'inline-block', color: COLORS.white, padding: '8px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', marginBottom: '20px' },
  badgeSecondary: { display: 'inline-block', padding: '8px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', marginBottom: '20px' },
  heroTitle: { fontSize: 'clamp(36px, 6vw, 56px)', color: COLORS.white, fontWeight: '700', marginBottom: '20px' },
  heroSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.7' },
  section: { padding: '80px 20px' },
  officesSection: { padding: '80px 20px' },
  mapSection: {},
  container: { maxWidth: '1200px', margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: '50px' },
  contactGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '60px' },
  infoSection: {},
  sectionTitle: { fontSize: '28px', fontWeight: '700', marginBottom: '10px' },
  sectionTitleCenter: { fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700' },
  sectionSubtitle: { fontSize: '16px', marginBottom: '40px' },
  infoCards: { display: 'flex', flexDirection: 'column', gap: '25px' },
  infoCard: { display: 'flex', gap: '20px', padding: '25px', borderRadius: '16px' },
  infoIcon: { fontSize: '32px' },
  infoTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '8px' },
  infoDetail: { fontSize: '14px', marginBottom: '4px' },
  socialSection: { marginTop: '40px', paddingTop: '30px' },
  socialTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '20px' },
  socialLinks: { display: 'flex', gap: '15px', flexWrap: 'wrap' },
  socialLink: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '50px', fontSize: '14px', textDecoration: 'none', transition: 'all 0.3s ease' },
  socialIcon: { fontWeight: '700' },
  formSection: {},
  formCard: { borderRadius: '24px', padding: '40px' },
  formTitle: { fontSize: '24px', fontWeight: '700', marginBottom: '30px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: '500' },
  input: { padding: '14px 18px', borderRadius: '12px', border: '1px solid', fontSize: '15px', outline: 'none', transition: 'border-color 0.3s ease' },
  select: { padding: '14px 18px', borderRadius: '12px', border: '1px solid', fontSize: '15px', outline: 'none', cursor: 'pointer' },
  textarea: { padding: '14px 18px', borderRadius: '12px', border: '1px solid', fontSize: '15px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  submitBtn: { color: COLORS.white, padding: '16px 32px', borderRadius: '50px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.3s ease', marginTop: '10px' },
  officesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '25px' },
  officeCard: { borderRadius: '16px', padding: '30px', textAlign: 'center' },
  officeFlagImage: { height: '48px', width: 'auto', marginBottom: '15px', borderRadius: '6px', boxShadow: '0 3px 12px rgba(0,0,0,0.15)' },
  officeCity: { fontSize: '20px', fontWeight: '600', marginBottom: '5px' },
  officeCountry: { fontSize: '14px', marginBottom: '12px' },
  officeType: { display: 'inline-block', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  mapPlaceholder: { height: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' },
  mapIcon: { fontSize: '64px', marginBottom: '20px' },
  mapTitle: { fontSize: '24px', color: COLORS.white, fontWeight: '600', marginBottom: '10px' },
  mapText: { fontSize: '16px', color: 'rgba(255,255,255,0.6)' }
}; 



export default Contact;